/**
 * Mechanic + lift assignment for one work order (#1984, #1993, #2647).
 *
 * One mechanic and one lift cover the whole work order. The selectors and the
 * eligibility rules lived inside ServiceStep (work-order creation) until
 * #2647 needed the same choice when a quote is accepted — the moment a quote
 * becomes real work, and the last moment before it lands on the calendar.
 * They are shared here so the two flows cannot drift: the same skill union,
 * the same lift-type intersection, the same MRU ordering.
 */

import { useTranslation } from 'react-i18next';
import {
  Alert,
  AlertTitle,
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { DROPDOWN_MRU_SELECTORS, useDropdownMru } from '@/hooks/useDropdownMru';
import { guideAnchor } from '@/lib/guides/engine';
import type { Service, ServiceSkillLink } from '@/hooks/useServices';
import type { MechanicWithSkills } from '@/hooks/useStaffUsers';
import { getLiftTypeLabel, type Lift } from '@/hooks/useLifts';
import { splitByEligibility } from '@/utils/skill-matching';
import {
  certificateRequiredSkills,
  mechanicCertificateIssue,
} from '../certificateGateError';

/** Order used to keep the strictest requirement when services overlap. */
const PROFICIENCY_ORDER = ['interest', 'beginner', 'intermediate', 'expert'];

function proficiencyRank(level: string): number {
  return PROFICIENCY_ORDER.indexOf(level);
}

/**
 * One mechanic takes the whole work order, so eligibility is the UNION of
 * every selected service's required skills — the mechanic must satisfy all of
 * them (epic #1140). Same rule as the backend's `resolveQualifiedMechanicIds`,
 * so the selector and the availability calculation cannot disagree (#1984).
 */
export function requiredSkillsForServices(
  serviceIds: readonly string[],
  services: readonly Service[],
): ServiceSkillLink[] {
  const byId = new Map<string, ServiceSkillLink>();
  for (const serviceId of serviceIds) {
    const svc = services.find((s) => s.id === serviceId);
    for (const link of svc?.skills ?? []) {
      if (!link.isRequired) continue;
      const existing = byId.get(link.skillId);
      // Keep the strictest requirement when two services want the same skill.
      if (!existing || proficiencyRank(link.minimumProficiency) > proficiencyRank(existing.minimumProficiency)) {
        byId.set(link.skillId, link);
      }
    }
  }
  return [...byId.values()];
}

/**
 * Lift types one lift can serve all selected services with (#1993).
 *
 * `ServiceLiftLink` rows say which types are ACCEPTABLE for a service, so the
 * single lift has to be acceptable to every service that names any — the
 * INTERSECTION, the mirror image of the skill union above. A service with no
 * lift rows accepts anything and does not narrow the set.
 *
 * `null` means "nothing narrows it": no selected service names a type. An
 * empty set means no single lift suits them all — the picker says so but
 * still does not block.
 */
export function acceptedLiftTypesForServices(
  serviceIds: readonly string[],
  services: readonly Service[],
): Set<string> | null {
  const perService = serviceIds
    .map((serviceId) => services.find((s) => s.id === serviceId)?.lifts ?? [])
    .filter((links) => links.length > 0)
    .map((links) => new Set(links.map((l) => l.liftType)));
  if (perService.length === 0) return null;
  return perService.reduce((acc, types) => new Set([...acc].filter((t) => types.has(t))));
}

/** Lock shown next to a still-selectable mechanic who lacks a valid certificate. */
export function MechanicCertificateLock({ blocked }: { blocked: boolean }) {
  const { t } = useTranslation('workorders');
  if (!blocked) return null;
  return (
    <Tooltip title={t('certificateGate.lockTooltip')}>
      <Box
        component="span"
        sx={{ display: 'inline-flex', ml: 1, color: 'warning.main' }}
        aria-label={t('certificateGate.lockTooltip')}
      >
        <LockOutlinedIcon fontSize="small" color="inherit" />
      </Box>
    </Tooltip>
  );
}

function MechanicOptionLabel({
  mechanic,
  requiredCertificateSkills,
}: {
  mechanic: MechanicWithSkills;
  requiredCertificateSkills: ServiceSkillLink[];
}) {
  const blocked = mechanicCertificateIssue(mechanic, requiredCertificateSkills) !== null;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
      <Box component="span" sx={{ flex: 1 }}>
        {mechanic.firstName} {mechanic.lastName}
      </Box>
      <MechanicCertificateLock blocked={blocked} />
    </Box>
  );
}

// ─── Mechanic select with skill filtering ────────────────────

export interface MechanicSelectProps {
  mechanicId: string;
  eligibleMechanics: MechanicWithSkills[];
  allMechanics: MechanicWithSkills[];
  /** Union of every selected service's required skills (#1984). */
  requiredSkills: ServiceSkillLink[];
  onChange: (mechanicId: string) => void;
}

export function MechanicSelect({
  mechanicId,
  eligibleMechanics,
  allMechanics,
  requiredSkills,
  onChange,
}: MechanicSelectProps) {
  const { t } = useTranslation('workorders');
  const mechanicMru = useDropdownMru(DROPDOWN_MRU_SELECTORS.mechanic);

  const hasSkillRequirements = requiredSkills.length > 0;
  const requiredCertificateSkills = certificateRequiredSkills(requiredSkills);
  const eligibleIds = new Set(eligibleMechanics.map((m) => m.id));
  const selectedIsIneligible = hasSkillRequirements && mechanicId !== '' && !eligibleIds.has(mechanicId);
  const ineligibleMechanics = allMechanics.filter((m) => !eligibleIds.has(m.id));
  const orderedEligible = mechanicMru.order(eligibleMechanics, (m) => m.id);
  const orderedIneligible = mechanicMru.order(ineligibleMechanics, (m) => m.id);

  return (
    <Box>
      <FormControl fullWidth size="small" {...guideAnchor('wo-create.mechanic')}>
        {/* labelId/id wire the label to the input — without them the select has
            no accessible name, which is also why it was unreachable by label
            in tests. */}
        <InputLabel id="wo-create-mechanic-label">{t('create.assignMechanic')}</InputLabel>
        <Select
          labelId="wo-create-mechanic-label"
          id="wo-create-mechanic"
          value={mechanicId}
          label={t('create.assignMechanic')}
          onChange={(e) => {
            const next = e.target.value;
            onChange(next);
            if (next !== '') mechanicMru.record(next);
          }}
        >
          <MenuItem value="">
            <em>{t('create.noMechanicAssigned')}</em>
          </MenuItem>
          {orderedEligible.map((m) => (
            <MenuItem key={m.id} value={m.id} sx={hasSkillRequirements ? { color: 'success.main', fontWeight: 500 } : undefined}>
              <MechanicOptionLabel mechanic={m} requiredCertificateSkills={requiredCertificateSkills} />
            </MenuItem>
          ))}
          {hasSkillRequirements && orderedIneligible.length > 0 && [
            <MenuItem key="__divider" disabled divider />,
            ...orderedIneligible.map((m) => (
              <MenuItem key={m.id} value={m.id} sx={{ color: 'text.secondary' }}>
                <MechanicOptionLabel mechanic={m} requiredCertificateSkills={requiredCertificateSkills} />
              </MenuItem>
            )),
          ]}
        </Select>
      </FormControl>
      {selectedIsIneligible && (
        <Typography variant="caption" color="warning.main" sx={{ mt: 0.5, display: 'block' }}>
          {t('create.mechanicMissingSkills')}
        </Typography>
      )}
    </Box>
  );
}

// ─── Lift select, suitable for every selected service ────────────────────

export interface LiftSelectProps {
  liftId: string;
  allLifts: Lift[];
  /**
   * Lift types acceptable to ALL selected services, or `null` when no service
   * names any (#1993). An empty set means the services want mutually
   * exclusive types, so no single lift suits them all.
   */
  acceptedLiftTypes: Set<string> | null;
  onChange: (liftId: string) => void;
}

export function LiftSelect({ liftId, allLifts, acceptedLiftTypes, onChange }: LiftSelectProps) {
  const { t } = useTranslation('workorders');

  const operationalLifts = allLifts.filter((l) => l.maintenanceStatus === 'operational');
  if (operationalLifts.length === 0) return null;

  const hasTypeRequirements = acceptedLiftTypes !== null;
  const suitableLifts = hasTypeRequirements
    ? operationalLifts.filter((l) => acceptedLiftTypes.has(l.type))
    : operationalLifts;
  const suitableIds = new Set(suitableLifts.map((l) => l.id));
  const otherLifts = operationalLifts.filter((l) => !suitableIds.has(l.id));
  const selectedIsUnsuitable = hasTypeRequirements && liftId !== '' && !suitableIds.has(liftId);

  const liftLabel = (l: Lift) => `${l.name} (${l.identifier}) — ${getLiftTypeLabel(l.type)}`;

  return (
    <Box>
      <FormControl fullWidth size="small">
        <InputLabel id="wo-create-lift-label">{t('create.assignLift')}</InputLabel>
        <Select
          labelId="wo-create-lift-label"
          id="wo-create-lift"
          value={liftId}
          label={t('create.assignLift')}
          onChange={(e) => onChange(e.target.value)}
        >
          <MenuItem value="">
            <em>{t('create.noLiftSelected')}</em>
          </MenuItem>
          {suitableLifts.map((l) => (
            <MenuItem
              key={l.id}
              value={l.id}
              sx={hasTypeRequirements ? { color: 'success.main', fontWeight: 500 } : undefined}
            >
              {liftLabel(l)}
            </MenuItem>
          ))}
          {hasTypeRequirements && otherLifts.length > 0 && [
            <MenuItem key="__divider" disabled divider />,
            ...otherLifts.map((l) => (
              <MenuItem key={l.id} value={l.id} sx={{ color: 'text.secondary' }}>
                {liftLabel(l)}
              </MenuItem>
            )),
          ]}
        </Select>
      </FormControl>
      {selectedIsUnsuitable && (
        <Typography variant="caption" color="warning.main" sx={{ mt: 0.5, display: 'block' }}>
          {t('create.liftNotSuitableForServices')}
        </Typography>
      )}
    </Box>
  );
}

// ─── Both together, with eligibility derived from the services ────────────

export interface ResourceAssignmentFieldsProps {
  /** Catalogue ids of the work order's services; drives skill and lift-type rules. */
  serviceIds: readonly string[];
  services: readonly Service[];
  mechanics: MechanicWithSkills[];
  lifts: Lift[];
  mechanicId: string;
  liftId: string;
  onMechanicChange: (mechanicId: string) => void;
  onLiftChange: (liftId: string) => void;
  /** Show the "usually planning does this" hint above the selectors. */
  showHint?: boolean;
}

/**
 * The two selectors as one block, eligibility computed from the services.
 * Used by AcceptQuoteScheduleDialog (#2647); ServiceStep composes the
 * selectors itself because it also owns the disclosure around them.
 */
export function ResourceAssignmentFields({
  serviceIds,
  services,
  mechanics,
  lifts,
  mechanicId,
  liftId,
  onMechanicChange,
  onLiftChange,
  showHint = true,
}: ResourceAssignmentFieldsProps) {
  const { t } = useTranslation('workorders');
  const requiredSkills = requiredSkillsForServices(serviceIds, services);
  const eligibleMechanics = splitByEligibility(mechanics, requiredSkills).eligible;
  const acceptedLiftTypes = acceptedLiftTypesForServices(serviceIds, services);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {showHint && (
        <Alert severity="info" variant="outlined">
          <AlertTitle sx={{ mb: 0 }}>{t('create.mechanicAndLiftHintTitle')}</AlertTitle>
          {t('create.mechanicAndLiftHint')}
        </Alert>
      )}
      <MechanicSelect
        mechanicId={mechanicId}
        eligibleMechanics={eligibleMechanics}
        allMechanics={mechanics}
        requiredSkills={requiredSkills}
        onChange={onMechanicChange}
      />
      <LiftSelect
        liftId={liftId}
        allLifts={lifts}
        acceptedLiftTypes={acceptedLiftTypes}
        onChange={onLiftChange}
      />
    </Box>
  );
}
